import { useCallback, useEffect, useState } from 'react';
import { isFunction, isUndefined } from 'lodash';
import { Logger } from '../../utilities';

/**
 * @description A hook to manage localStorage state
 * @param key - The key to store the value in localStorage
 * @param initialValue - The initial value to store in localStorage
 * @returns A tuple containing the current value and a function to update the value
 */
export const useLocalStorage = <T = any>(key: string, initialValue?: T) => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      Logger.debug(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(JSON.parse(e.newValue));
        } catch (error) {
          Logger.debug(`Error parsing localStorage value for key "${key}":`, error);
        }
      }
    };

    const handleCustomStorageChange = (e: CustomEvent) => {
      if (e.detail.key === key) {
        setStoredValue(e.detail.value);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('localStorageChange', handleCustomStorageChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange as EventListener);
    };
  }, [key]);

  const setValue = useCallback((value: T | ((prevValue: T) => T)) => {
    try {
      setStoredValue(prevStoredValue => {
        const valueToStore = isFunction(value) ? value(prevStoredValue) : value;

        if (!isUndefined(valueToStore)) {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));

          window.dispatchEvent(new CustomEvent('localStorageChange', {
            detail: { key, value: valueToStore }
          }));

          return valueToStore;
        }

        return prevStoredValue;
      });
    } catch (error) {
      Logger.debug(`Error setting localStorage key "${key}":`, error);
    }
  }, [key]);

  const getCurrentValue = useCallback((): T => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : storedValue;
    } catch (error) {
      Logger.debug(`Error reading current localStorage key "${key}":`, error);
      return storedValue;
    }
  }, [key, storedValue]);

  return [storedValue, setValue, getCurrentValue] as const;
};

export default useLocalStorage;
